import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type FlowModel from 'garaje/models/flow';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface WelcomeEmailComponentSignature {
  Args: {
    isDeferedToFlowSettings: boolean;
    flow: FlowModel;
    onChange: (args: { hasChanges: boolean }) => void;
    readOnly: boolean;
    unchanged: boolean;
  };
}

export default class WelcomeEmailComponent extends Component<WelcomeEmailComponentSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;

  @tracked isLoading = false;
  @tracked isOpen = false;
  @tracked showUnsavedEditsWarning = false;

  updateFlowWelcomeEmailConfigAndSave = dropTask(async () => {
    try {
      const hasDirtyWelcomeEmailPreference = this.args.flow.hasDirtyWelcomeEmailPreference();
      if (hasDirtyWelcomeEmailPreference) {
        await this.args.flow.save();
        this.metrics.trackEvent('Welcome Email Edited - Flow', {
          flow_id: this.args.flow.id,
          flow_name: this.args.flow.name,
          recipients_edited: hasDirtyWelcomeEmailPreference,
        });
      }
      this.isOpen = false;
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  onContinue(): void {
    if (<boolean>(<unknown>this.args.flow.hasDirtyAttributes)) {
      this.args.flow.rollbackAttributes();
    }
    this.showUnsavedEditsWarning = false;
    this.isOpen = false;
    this.args.onChange({ hasChanges: false });
  }

  @action
  cancel(): void {
    if (<boolean>(<unknown>this.args.flow.hasDirtyAttributes)) {
      this.showUnsavedEditsWarning = true;
    } else {
      this.onContinue();
    }
  }

  @action
  changePreference(value: string): void {
    this.args.flow.welcomeEmailPreference = value;
    this.args.onChange({ hasChanges: true });
  }
}
